import { useEffect, useMemo, useState } from 'react'
import {
  adminGetUser,
  adminListUsers,
  adminSetPlanOverride,
  adminActAs,
  adminSetUserFlags,
  useIsAdmin,
} from '../../lib/admin'
import type { AdminUserDetail, AdminUserRow } from '../../lib/admin'
import { useAuth } from '../../lib/auth'
import { DEFAULT_PLANS, FREE_PLAN_ID } from '../../lib/plans'
import Icon from '../../components/Icon'
import Modal from '../../components/Modal'
import { AdminPage } from './Admin'
import { Initials } from '../database/lists'
import SearchField from '../../components/SearchField'

/**
 * Every account, and one account in detail.
 *
 * The detail panel exists to answer "why is theirs broken" — their tours, the
 * tours they're crew on, whether they ever finished onboarding, whether their
 * email is verified, how many push tokens they have. Those five things explain
 * most support mail.
 */
export default function AdminUsers() {
  const { user: me } = useAuth()
  const [rows, setRows] = useState<AdminUserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<'all' | 'admins' | 'unverified' | 'inactive'>('all')
  const [selected, setSelected] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await adminListUsers({})
      setRows(data.users)
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return rows
      .filter((row) => {
        if (filter === 'admins' && !row.isAdmin) return false
        if (filter === 'unverified' && row.emailVerified) return false
        // Signed up but never built anything — the cohort worth understanding.
        if (filter === 'inactive' && row.tourCount > 0) return false
        if (!needle) return true
        return (
          (row.email ?? '').toLowerCase().includes(needle) ||
          (row.displayName ?? '').toLowerCase().includes(needle) ||
          row.uid.toLowerCase().includes(needle)
        )
      })
      .sort((a, b) => Date.parse(b.createdAt ?? '0') - Date.parse(a.createdAt ?? '0'))
  }, [rows, query, filter])

  const counts = {
    all: rows.length,
    admins: rows.filter((row) => row.isAdmin).length,
    unverified: rows.filter((row) => !row.emailVerified).length,
    inactive: rows.filter((row) => row.tourCount === 0).length,
  }

  return (
    <AdminPage
      title="Users"
      lede="Listed from Firebase Auth, not Firestore — an account with no user document is exactly the kind you came here to find."
      actions={
        <button className="btn btn-secondary" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      }
    >
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SearchField
          label="Search email, name or uid"
          value={query}
          onChange={setQuery}
          width={300}
        />
        <div className="row scroll-x" style={{ gap: 7 }}>
          {(
            [
              ['all', 'Everyone'],
              ['admins', 'Admins'],
              ['unverified', 'Unverified'],
              ['inactive', 'No tours'],
            ] as const
          ).map(([value, label]) => {
            const isSelected = filter === value
            return (
              <button
                key={value}
                onClick={() => setFilter(value)}
                aria-pressed={isSelected}
                style={{
                  flex: 'none',
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-chip)',
                  fontSize: '0.8rem',
                  fontWeight: isSelected ? 600 : 400,
                  background: 'var(--panel)',
                  border: `${isSelected ? '2px' : '1px'} solid ${
                    isSelected ? 'var(--accent)' : 'var(--hairline)'
                  }`,
                }}
              >
                {label}
                <span className="t-spec-caption faint" style={{ marginLeft: 6 }}>
                  {counts[value]}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {error && (
        <p className="panel panel-pad t-caption" style={{ color: 'var(--destructive)' }}>
          {error}
        </p>
      )}

      {!loading && visible.length === 0 && !error && (
        <p className="panel panel-pad t-caption">Nobody matches that.</p>
      )}

      {visible.length > 0 && (
        <div className="panel rows">
          {visible.map((row) => (
            <button
              key={row.uid}
              onClick={() => setSelected(row.uid)}
              className="row"
              style={{ width: '100%', padding: '13px 16px', gap: 13, textAlign: 'left' }}
            >
              <Initials name={row.displayName || row.email || '?'} />
              <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
                <span className="truncate" style={{ fontWeight: 600 }}>
                  {row.displayName || '(no name)'}
                </span>
                <span className="t-caption truncate">{row.email ?? row.uid}</span>
              </span>

              <span className="row" style={{ gap: 6, flex: 'none' }}>
                {row.isSuperAdmin ? (
                  <span className="chip chip-accent">Super admin</span>
                ) : (
                  row.isAdmin && <span className="chip">Admin</span>
                )}
                {row.disabled && <span className="chip chip-warning">Disabled</span>}
                {!row.emailVerified && <span className="chip">Unverified</span>}
                <span className="t-spec-caption faint">
                  {row.tourCount} {row.tourCount === 1 ? 'tour' : 'tours'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <UserDetail
          uid={selected}
          isSelf={selected === me?.uid}
          onClose={() => setSelected(null)}
          onChanged={() => void load()}
        />
      )}
    </AdminPage>
  )
}

function UserDetail({
  uid,
  isSelf,
  onClose,
  onChanged,
}: {
  uid: string
  isSelf: boolean
  onClose: () => void
  onChanged: () => void
}) {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    setError(null)
    try {
      setDetail(await adminGetUser({ uid }))
    } catch (caught) {
      setError((caught as Error).message)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  async function setFlags(flags: {
    disabled?: boolean
    isAdmin?: boolean
    isSuperAdmin?: boolean
  }) {
    setBusy(true)
    setError(null)
    try {
      await adminSetUserFlags({ uid, ...flags })
      await load()
      onChanged()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const { isSuperAdmin } = useIsAdmin()
  const { refreshClaims } = useAuth()
  const account = detail?.account
  const orgId = (detail?.profile?.orgId as string | undefined) ?? null

  async function buildInAccount() {
    if (!account) return

    const label = account.email ?? account.displayName ?? 'this account'
    const confirmed = window.confirm(
      `Build inside ${label}?\n\n` +
        'This is a live customer account. Everything you create, edit or delete ' +
        'while you are in there is theirs, and it is recorded in the audit log.',
    )
    if (!confirmed) return

    setBusy(true)
    setError(null)
    try {
      // Open-ended. During the beta a super admin needs to sit in an account
      // long enough to reproduce a bug or finish a backfill, and a grant that
      // expires mid-import just starts failing writes.
      const grant = await adminActAs({ uid })
      sessionStorage.setItem('encore.actingAs', grant.orgName)
      // The claim is set server-side. Without forcing a refresh the browser
      // keeps the token it already had — for up to an hour — so the rules
      // never see the grant and the app opens as yourself.
      await refreshClaims()
      window.location.assign('/app')
    } catch (caught) {
      setError((caught as Error).message)
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      width={640}
      title={account?.displayName || account?.email || 'Account'}
      onClose={onClose}
      footer={
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      {!detail && !error && <p className="t-caption">Loading…</p>}
      {error && (
        <p className="t-caption" style={{ color: 'var(--destructive)' }}>
          {error}
        </p>
      )}

      {detail && account && (
        <div className="stack" style={{ gap: 18 }}>
          <div
            className="responsive-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}
          >
            <Field label="Email" value={account.email} />
            <Field label="User ID" value={account.uid} mono />
            <Field label="Signed up" value={formatStamp(account.createdAt)} />
            <Field label="Last signed in" value={formatStamp(account.lastSignInAt)} />
            <Field label="Sign-in method" value={account.providers.join(', ') || '—'} />
            <Field label="Push tokens" value={String(detail.fcmTokenCount)} />
          </div>

          <div className="row" style={{ gap: 7, flexWrap: 'wrap' }}>
            {account.isSuperAdmin ? (
              <span className="chip chip-accent">Super admin</span>
            ) : (
              account.isAdmin && <span className="chip">Admin</span>
            )}
            {account.disabled && <span className="chip chip-warning">Disabled</span>}
            <span className={account.emailVerified ? 'chip chip-positive' : 'chip chip-warning'}>
              {account.emailVerified ? 'Email verified' : 'Email unverified'}
            </span>
            {/* No `role` field means they never finished the onboarding survey
                and the native apps will keep showing it to them. */}
            <span className={detail.profile?.role ? 'chip' : 'chip chip-warning'}>
              {detail.profile?.role ? String(detail.profile.role) : 'Never onboarded'}
            </span>
          </div>

          <section className="stack" style={{ gap: 8 }}>
            <span className="t-eyebrow">Owns {detail.ownedTours.length} tours</span>
            {detail.ownedTours.length === 0 ? (
              <p className="t-caption faint">Hasn't created a tour.</p>
            ) : (
              <div className="panel rows">
                {detail.ownedTours.map((tour) => (
                  <div key={tour.id} className="spread" style={{ padding: '10px 14px', gap: 10 }}>
                    <span className="truncate">{tour.tourName}</span>
                    <span className="t-spec-caption faint">{tour.artist}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="stack" style={{ gap: 8 }}>
            <span className="t-eyebrow">Crew on {detail.crewOn.length} tours</span>
            {detail.crewOn.length === 0 ? (
              <p className="t-caption faint">Not on anyone else's tour.</p>
            ) : (
              <div className="panel rows">
                {detail.crewOn.map((entry) => (
                  <div key={entry.id} className="row" style={{ padding: '10px 14px', gap: 10 }}>
                    <span className="grow truncate">{entry.tourName}</span>
                    {entry.status !== 'accepted' && (
                      <span className="chip chip-warning">{entry.status}</span>
                    )}
                    <span className="chip">{entry.accessLevel ?? 'viewer (implied)'}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <PlanControl
            uid={uid}
            subscription={detail.profile?.subscription as StoredSubscription | undefined}
            override={detail.profile?.planOverride as StoredOverride | undefined}
            onChanged={() => void load()}
          />

          <section className="stack" style={{ gap: 8 }}>
            <span className="t-eyebrow">Actions</span>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary"
                disabled={busy || isSelf || !isSuperAdmin}
                title={
                  isSelf
                    ? "You can't change your own access"
                    : !isSuperAdmin
                      ? 'Only a super admin can change access'
                      : undefined
                }
                onClick={() => void setFlags({ isAdmin: !account.isAdmin })}
              >
                <Icon name="account" size={15} />
                {account.isAdmin ? 'Revoke admin' : 'Make admin'}
              </button>

              {/* Acting inside a live customer account, as opposed to a
                  prospect nobody owns yet. Same mechanism, different weight —
                  so it confirms by name, runs shorter, and the banner says
                  whose account you are in rather than "another account". */}
              <button
                className="btn btn-secondary"
                disabled={busy || isSelf || !isSuperAdmin}
                title={
                  !isSuperAdmin
                    ? 'Only a super admin can do that'
                    : isSelf
                      ? "You're already in your own account"
                      : undefined
                }
                onClick={() => void buildInAccount()}
              >
                <Icon name="edit" size={15} />
                Build in this account
              </button>

              {/* Super admin is the tier that can grant access and build
                  inside somebody else's account. Granting it implies admin;
                  revoking admin takes it away with it. */}
              <button
                className="btn btn-secondary"
                disabled={busy || isSelf || !isSuperAdmin}
                title={
                  isSelf
                    ? "You can't change your own access"
                    : !isSuperAdmin
                      ? 'Only a super admin can change access'
                      : undefined
                }
                onClick={() => void setFlags({ isSuperAdmin: !account.isSuperAdmin })}
              >
                <Icon name="key" size={15} />
                {account.isSuperAdmin ? 'Revoke super admin' : 'Make super admin'}
              </button>

              <button
                className={account.disabled ? 'btn btn-secondary' : 'btn btn-danger'}
                disabled={busy || isSelf}
                onClick={() => void setFlags({ disabled: !account.disabled })}
              >
                {account.disabled ? 'Re-enable account' : 'Disable account'}
              </button>
            </div>
            {/* A disabled button whose reason lives in a hover tooltip is a
                dead end — you press it, nothing happens, and there is nowhere
                to find out why. */}
            <p className="t-caption faint">
              {isSelf
                ? "This is the account you're signed in as, so building in it and changing its access are both disabled."
                : !isSuperAdmin
                  ? 'Granting access and building in an account both need super admin.'
                  : !orgId
                    ? 'This account has never opened Encore, so it has no organisation yet. Building in it will create one.'
                    : 'A newly granted admin has to sign out and back in before the claim reaches their session. Every action here is written to the audit log.'}
            </p>
          </section>
        </div>
      )}
    </Modal>
  )
}

/* -------------------------------------------------------------------- plan */

interface StoredSubscription {
  source?: string
  planId?: string
  status?: string
}

interface StoredOverride {
  planId?: string
  expiresAt?: number | null
  note?: string
  setBy?: string
}

/** Durations worth one click. Anything else is a plan change, not a comp. */
const DURATIONS: Array<{ days: number | null; label: string }> = [
  { days: 30, label: '30 days' },
  { days: 90, label: '3 months' },
  { days: 365, label: '1 year' },
  { days: null, label: 'No expiry' },
]

/**
 * Override an account's plan.
 *
 * Writes `planOverride`, never the subscription record. Billing owns that
 * field and would overwrite a comp the next time a webhook fired — an
 * override sits beside it, and whichever grants more wins. That is what lets
 * this work on a paying account, and what makes it reversible: clearing it
 * drops them back to exactly what they pay for.
 *
 * It can only grant MORE. Downgrading someone who is paying is not a thing
 * this can do, by construction.
 */
function PlanControl({
  uid,
  subscription,
  override,
  onChanged,
}: {
  uid: string
  subscription?: StoredSubscription
  override?: StoredOverride
  onChanged: () => void
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [days, setDays] = useState<number | null>(null)
  const [note, setNote] = useState('')

  const paying = subscription?.planId ?? FREE_PLAN_ID
  const granted = override?.planId ?? null
  const effective =
    [paying, granted]
      .filter((id): id is string => Boolean(id))
      .map((id) => DEFAULT_PLANS.find((plan) => plan.id === id))
      .filter((plan): plan is (typeof DEFAULT_PLANS)[number] => Boolean(plan))
      .sort((a, b) => b.rank - a.rank)[0] ?? DEFAULT_PLANS[0]

  async function apply(planId: string | null) {
    setBusy(planId ?? 'clear')
    setError(null)
    try {
      await adminSetPlanOverride({ uid, planId, days, note: note.trim() || undefined })
      setNote('')
      onChanged()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="stack" style={{ gap: 10 }}>
      <div className="spread" style={{ alignItems: 'baseline', gap: 10 }}>
        <span className="t-eyebrow">Plan</span>
        <span className="t-spec-caption faint">
          On {effective.displayName}
          {granted ? ' · overridden' : ''}
        </span>
      </div>

      <div className="panel panel-pad stack" style={{ gap: 12 }}>
        <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>
          <span className="t-caption">
            Paying for <strong>{paying}</strong>
            {subscription?.source ? ` via ${subscription.source}` : ' — nothing on file'}
            {subscription?.status ? ` (${subscription.status})` : ''}
          </span>
        </div>

        {granted && (
          <div
            className="stack"
            style={{
              gap: 4,
              padding: 12,
              borderRadius: 'var(--radius-control)',
              background: 'var(--accent-wash)',
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--accent-text)' }}>
              Overridden to {granted}
            </span>
            <span className="t-spec-caption">
              {override?.expiresAt
                ? `Until ${new Date(override.expiresAt).toLocaleDateString()}`
                : 'No expiry'}
              {override?.setBy ? ` · set by ${override.setBy}` : ''}
            </span>
            {override?.note && <span className="t-caption">{override.note}</span>}
            <button
              className="btn btn-secondary"
              style={{ alignSelf: 'flex-start', marginTop: 4 }}
              disabled={busy !== null}
              onClick={() => void apply(null)}
            >
              {busy === 'clear' ? 'Clearing…' : 'Remove override'}
            </button>
          </div>
        )}

        <div className="stack" style={{ gap: 6 }}>
          <span className="t-eyebrow">For how long</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {DURATIONS.map((option) => (
              <Chip
                key={option.label}
                label={option.label}
                on={days === option.days}
                onClick={() => setDays(option.days)}
              />
            ))}
          </div>
        </div>

        <label className="stack" style={{ gap: 6 }}>
          <span className="t-eyebrow">Why</span>
          <input
            className="field"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <span className="t-spec-caption faint">
            Optional, but it is what the audit log and the next admin will read.
          </span>
        </label>

        <div className="stack" style={{ gap: 6 }}>
          <span className="t-eyebrow">Upgrade to</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {DEFAULT_PLANS.filter((plan) => plan.id !== FREE_PLAN_ID).map((plan) => (
              <button
                key={plan.id}
                type="button"
                className="btn btn-secondary"
                disabled={busy !== null}
                onClick={() => void apply(plan.id)}
              >
                {busy === plan.id ? 'Granting…' : plan.displayName}
                {plan.maxArtists !== null && plan.maxArtists > 1 ? ` ${plan.maxArtists}` : ''}
              </button>
            ))}
          </div>
        </div>

        <p className="t-caption faint">
          An override sits alongside billing rather than replacing it, so a renewal can't wipe
          it and removing it returns the account to whatever it actually pays for. It only ever
          grants more — it cannot downgrade a paying customer.
        </p>
      </div>

      {error && (
        <span className="t-caption" style={{ color: 'var(--destructive)' }}>
          {error}
        </span>
      )}
    </section>
  )
}

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className="chip"
      aria-pressed={on}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        background: on ? 'var(--accent-wash)' : 'transparent',
        border: `var(--hairline-width) solid ${on ? 'var(--accent)' : 'var(--hairline)'}`,
        color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
      }}
    >
      {label}
    </button>
  )
}

function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div className="stack" style={{ gap: 1, minWidth: 0 }}>
      <span className="t-eyebrow">{label}</span>
      <span
        className={mono ? 't-spec-caption' : 't-body'}
        style={{ wordBreak: 'break-all' }}
      >
        {value || '—'}
      </span>
    </div>
  )
}

function formatStamp(value?: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date)
}
